import React from 'react';
import { ApolloError } from '@apollo/client';
import {
  AnalyticsEventSource,
  EntitlementPageType,
  PAGETITLE,
  ScreenEventAnalytics,
  ScreenType,
} from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { AllowedSidebarMap, AppContent, AppPage, PageHeaderV2 } from '@css-xp/base';
import EntitlementBreadcrumbs, { BreadcrumbItem } from '@css-xp/base/breadcrumbs/src/breadcrumbs';
import { ErrorPanel } from '@css-xp/base/error-page';
import { EntitlementDetails as Entitlement } from '@css-xp/graphtypes/src/generated-types-new';
import { HeaderIcon } from '../common/components/HeaderIcon';
import EntitlementPageBanner from './entitlement-page-banner';
import EntitlementPageInfoPanel from './entitlement-page-info-panel';

interface Props {
  allowedSidebars?: AllowedSidebarMap;
  entitlement?: Entitlement;
  entitlementType?: EntitlementPageType;
  appPageClass?: string;
}

export const EntitlementPageUtility = ({
  children,
  entitlement,
  entitlementType,
  allowedSidebars,
  appPageClass,
}: React.PropsWithChildren<Props>) => {
  if (!entitlement) {
    return null;
  }
  const { baseProduct, productId, id, platform } = entitlement;

  return (
    <>
      <EntitlementPageBanner entitlement={entitlement} />
      <EntitlementPageInfoPanel entitlement={entitlement} />
      <AppPage id="entitlement-page" allowedSidebars={allowedSidebars} className={appPageClass}>
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.ENTITLEMENT_SCREEN,
            attributes: {
              baseProduct,
              productId,
              id,
              entityName: PAGETITLE.SUBSCRIPTION,
              pageType: entitlementType || EntitlementPageType.GENERIC,
              platform,
              screenType: ScreenType.V2,
              entitlementNumber: entitlement.entitlementNumber,
            },
          }}
        />
        <AppContent>{children}</AppContent>
      </AppPage>
    </>
  );
};

interface HeaderProps {
  header?: React.ReactNode;
  subheader?: React.ReactNode;
  actions?: React.ReactNode;
  entitlement?: Entitlement;
}

EntitlementPageUtility.Header = function Header({ header, subheader, actions, entitlement }: HeaderProps) {
  if (!entitlement) return null;

  return (
    <PageHeaderV2 icon={<HeaderIcon {...entitlement} />} header={header} subheader={subheader} actions={actions} />
  );
};

interface SummaryProps {
  EntitlementSummary: React.ComponentType<object>;
}

EntitlementPageUtility.Summary = function Summary({ EntitlementSummary }: SummaryProps) {
  return <EntitlementSummary />;
};

interface TabsProps {
  EntitlementTabs: React.ComponentType<object>;
  refetch?: React.ReactNode;
}

EntitlementPageUtility.Tabs = function Tabs({ EntitlementTabs }: TabsProps) {
  return <EntitlementTabs />;
};

EntitlementPageUtility.Loading = function Loading() {
  return <LoadingIndicator />;
};

interface EntitlementErrorProps {
  error: Error | ApolloError;
}
EntitlementPageUtility.Error = function Error({ error }: EntitlementErrorProps) {
  return (
    <div data-testid="error-panel">
      <ErrorPanel error={error} />
    </div>
  );
};

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

EntitlementPageUtility.Breadcrumbs = function Breadcrumbs({ items }: BreadcrumbsProps) {
  return <EntitlementBreadcrumbs items={items} />;
};
